import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PLATFORM_KINDS, platformKindOverview, platformSpendByKind, type Platform, type PlatformKind, type PlatformReport } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { money, monthLabel } from "../lib/format";
import { KIND_COLORS, currencyTotals } from "../lib/platformBilling";
import { ui } from "../theme";
import { PLATFORMS_NS } from "../locales/platformsI18n";

// Plataformas vs. infraestructura: dos pagos distintos que comparten catálogo.
// Plataforma = herramienta con la que trabaja el equipo (gasto de operación).
// Infraestructura = lo que corre o consume el producto (costo del servicio).
// Las tarjetas filtran la sección; la gráfica muestra lo pagado por mes de cada tipo.

/** Meses que muestra la gráfica (los más recientes del reporte). */
const MONTHS_SHOWN = 12;

const kFormat = (v: unknown) => {
  const n = Number(v);
  return Math.abs(n) >= 1000 ? `$${Math.round(n / 1000)}k` : `$${Math.round(n)}`;
};

export default function PlatformKindSummary({
  platforms,
  report,
  rates,
  today,
  value,
  onChange,
}: {
  /** Plataformas que pasan los demás filtros (sin el de tipo). */
  platforms: Platform[];
  /** Reporte de lo pagado de esas mismas plataformas; `null` mientras carga. */
  report: PlatformReport[] | null;
  rates: Record<string, number>;
  today: string;
  value: PlatformKind | "";
  onChange: (next: PlatformKind | "") => void;
}) {
  const { t: tp, i18n } = useTranslation(PLATFORMS_NS);
  const language = i18n.language;
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const overview = useMemo(() => platformKindOverview(platforms, rates), [platforms, rates]);
  const spend = useMemo(() => (report ? platformSpendByKind(report, platforms, today) : null), [report, platforms, today]);
  const chart = useMemo(
    () => (spend ? spend.monthly.slice(-MONTHS_SHOWN).map((m) => ({ ...m, label: monthLabel(m.period, language) })) : []),
    [spend, language],
  );
  const year = today.slice(0, 4);
  const hasPaid = chart.some((m) => m.plataforma > 0 || m.infraestructura > 0);
  const axis = { fontSize: 11, fill: isDarkMode ? "#9ca3af" : "#6b7280" };
  const grid = isDarkMode ? "#333" : "#e5e7eb";
  const share = spend?.infrastructureShare;

  const card = (kind: PlatformKind | "") => {
    const o = kind ? overview[kind] : null;
    const count = o ? o.activeCount : overview.plataforma.activeCount + overview.infraestructura.activeCount;
    const paid = spend ? (kind ? spend.ytd[kind] : spend.ytd.plataforma + spend.ytd.infraestructura) : null;
    const selected = value === kind;
    return (
      <button
        key={kind || "todas"}
        type="button"
        role="radio"
        aria-checked={selected}
        data-testid={`kind-${kind || "todas"}`}
        onClick={() => onChange(kind)}
        className={`rounded-xl border p-3 text-left transition-colors ${selected ? "border-teal-500 bg-teal-500/10" : `${c.border} ${c.rowHover}`}`}
      >
        <span className={`flex items-center gap-2 text-sm font-semibold ${c.text}`}>
          {kind && <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: KIND_COLORS[kind] }} />}
          {tp(kind ? `kind.${kind}Plural` : "kind.all")}
          <span className={`ml-auto text-xs font-normal ${c.muted}`}>{tp("kind.active", { count })}</span>
        </span>
        <span className={`mt-1 block text-xs ${c.muted}`}>{tp(kind ? `kind.${kind}Hint` : "kind.allHint")}</span>
        {o && <span className={`mt-2 block text-sm tabular-nums ${c.text}`}>{`${tp("kind.monthly")}: ${currencyTotals(o.monthlyByCurrency, language)}`}</span>}
        {paid != null && <span className={`block text-sm tabular-nums ${c.text}`}>{`${tp("kind.paidYtd", { year })}: ${money(paid, "MXN", language)}`}</span>}
      </button>
    );
  };

  return (
    <section className={c.card} aria-label={tp("kind.title")} data-testid="platform-kind-summary">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className={`text-sm font-semibold ${c.text}`}>{tp("kind.title")}</h2>
        {share != null && <p className={`text-xs ${c.muted}`}>{tp("kind.share", { year, percent: Math.round(share * 100) })}</p>}
      </div>
      <p className={`mb-3 text-xs ${c.muted}`}>{tp("kind.subtitle")}</p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3" role="radiogroup" aria-label={tp("kind.title")}>
        {card("")}
        {PLATFORM_KINDS.map((k) => card(k))}
      </div>
      {hasPaid && (
        <div className="mt-4">
          <p className={`mb-1 text-xs ${c.muted}`}>{tp("kind.chartTitle")}</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                <XAxis dataKey="label" tick={axis} interval="preserveStartEnd" minTickGap={8} />
                <YAxis tick={axis} tickFormatter={kFormat} width={52} />
                <Tooltip
                  formatter={(v, name) => [money(Number(v), "MXN", language), tp(`kind.${String(name)}Plural`)]}
                  contentStyle={{ fontSize: 12, backgroundColor: isDarkMode ? "#1f1f1f" : "#fff", borderColor: grid, color: isDarkMode ? "#fff" : "#111" }}
                  cursor={{ fill: isDarkMode ? "#ffffff10" : "#0000000a" }}
                />
                <Legend formatter={(v: string) => tp(`kind.${v}Plural`)} wrapperStyle={{ fontSize: 11 }} />
                {/* Infraestructura abajo (base del costo del servicio), plataformas encima; 2 px de separación entre segmentos. */}
                <Bar dataKey="infraestructura" stackId="kind" fill={KIND_COLORS.infraestructura} stroke={isDarkMode ? "#1a1a19" : "#fff"} strokeWidth={1} maxBarSize={28} />
                <Bar dataKey="plataforma" stackId="kind" fill={KIND_COLORS.plataforma} stroke={isDarkMode ? "#1a1a19" : "#fff"} strokeWidth={1} radius={[4, 4, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </section>
  );
}
